import { Button } from '@mui/material'
import { StyledEngineProvider, ThemeProvider, adaptV4Theme, createTheme } from '@mui/material/styles'
import { withSnackbar } from 'notistack'
import React from "react"
import { withRouter } from "react-router-dom"
import SharedUtilities from '../../shared/classes/SharedUtilities'
import SocketIO from '../socketio'
import wylieTheme from '../themes/default.js'
import App from '../views/App'
import CaregiverApp from '../views/Caregiver/App'
import EmployeeApp from '../views/Employee/App'

export const AppContext = React.createContext()

const defaultTheme = createTheme(adaptV4Theme(wylieTheme))

class AppContextProvider extends React.Component {
    socket = null

    state = {
        connected: false,
        initialized: false,
        companionReady: false,
        forgotPasswordCode: null,
        store: null,
        stores: [],
        settings: null,
        features: null,
        user: null,
        userType: null,
        remoteUser: null,
        cart: null,
        theme: defaultTheme,
        isEmbedded: this.props.isEmbedded,
        embedOptions: {
            minimalMode: false,
            bookingLandingUrl: null,
            orderLandingUrl: null,
            leadLandingUrl: null,
            leadFormOptionals: null,
        },
    }

    // fixes that prevent parent from being scrolled by certain components gaining focus
    iframeFixes = this.props.isEmbedded ? {
        // for <Menu> components
        Menu: {
            disableAutoFocus: true,
            MenuListProps: {
                autoFocusItem: false,
            },
        },
        // for <Select> components
        Select: {
            MenuProps: {
                autoFocus: false,
                disableAutoFocusItem: true,
                disableEnforceFocus: true,
                disableAutoFocus: true,
                MenuListProps: {
                    autoFocusItem: false,
                },
            },
        }
    } : {}

    supportedEmbedOptions = {
        minimalMode: 'boolean',
        bookingLandingUrl: 'string',
        orderLandingUrl: 'string',
        leadLandingUrl: 'string',
        leadFormOptionals: 'string'
    }

    embedOptionProcessors = {
        string: (value) => value,
        boolean: (value) => value === 'true',
    }

    constructor(props) {
        super(props)

        const { match: { params, url } } = props

        this.fileset = params.fileset
        this.storeId = params.storeId
        this.paths = {
            root: `${url}`,
            register: `${url}/register`,
            authorizeDevice: `${url}/authorizeDevice`,
            forgotPassword: `${url}/forgotPassword`,
            account: `${url}/account`,
            agenda: `${url}/agenda`,
            verifySMS: `${url}/verifySMS`,
            changePassword: `${url}/changePassword`,
            book: `${url}/book`,
            order: `${url}/order`,
            cart: `${url}/cart`,
            appointments: `${url}/appointments`,
            orders: `${url}/orders`,
            addPaymentMethod: `${url}/addPaymentMethod`,
            paymentMethods: `${url}/paymentMethods`,
            leadForm: `${url}/leadForm`,

            // provider portal stuff
            patients: `${url}/patients`,
            caregiverQRCodes: `${url}/caregiverQRCodes`,
        }
    }

    componentDidMount = async () => {
        window.addEventListener('message', this.handlePostMessage)

        this.socket = new SocketIO()

        this.unbindConnect = this.socket.on('connect', this.handleConnect)
        this.unbindDisconnect = this.socket.on('disconnect', this.handleDisconnect)
        this.unbindSession = this.socket.on('session', this.handleSessionUpdate)
        this.unbindCartUpdate = this.socket.on('cartUpdate', this.handleCartUpdate)

        try {
            // check for 'forgotPasswordCode' in url and go to 'forgotPassword' view if present
            if (SharedUtilities.urlParamExists('forgotPasswordCode')) {
                await this.setState({
                    forgotPasswordCode: SharedUtilities.getBase64JsonUrlParam('forgotPasswordCode'),
                })

                this.props.history.push(this.paths.forgotPassword)
            }

            // check for 'verification' in url and forward verification code to backend if present
            if (SharedUtilities.urlParamExists('verification')) {
                const verification = SharedUtilities.getBase64JsonUrlParam('verification')
                const response = await this.socket.send('register', { step: 'verifyCode', ...verification })
                this.showMessage(response, { variant: 'success' })
            }
        } catch (err) {
            this.showError(err)
        }
    }

    componentWillUnmount = () => {
        window.removeEventListener('message', this.handlePostMessage)

        this.unbindConnect()
        this.unbindSession()
        this.unbindDisconnect()

        this.socket.disconnect()
    }

    initialFetch = async () => {
        try {
            const { store, stores, settings, features, user, remoteUser, cart } = await this.socket.send('init')

            document.title = store.name

            if (settings.theme) {
                await this.setState({
                    theme: createTheme(adaptV4Theme({ ...wylieTheme, ...JSON.parse(settings.theme) })),
                })
            }

            // this can also be overridden by the companion in handlePostMessage
            if (this.state.isEmbedded && settings.minimal_iframe == '1') {
                await this.setState({
                    embedOptions: {
                        ...this.state.embedOptions,
                        minimalMode: true
                    },
                })
            }

            if (this.state.isEmbedded) {
                console.log('Looking for Wylie Companion')

                // see if the companion is loaded
                this.messageCompanion({
                    ping: true,
                })
            }

            await this.setState({
                connected: true,
                initialized: store ? true : false,
                store,
                stores,
                settings,
                features,
                user,
                userType: findUserType(user),
                remoteUser,
                cart,
            })

        } catch (err) {
            this.showError(err)
        }
    }

    handleConnect = () => {
        this.initialFetch()
    }

    handleDisconnect = () => {
        this.setState({
            connected: false,
        })
    }

    handleSessionUpdate = ({ user, remoteUser, cart }) => {
        const { history, location } = this.props

        this.setState({
            user,
            userType: findUserType(user),
            remoteUser,
            cart,
        })

        // go to to previous view if session was populated
        if (user && !this.state.user) {
            if (location.state) {
                history.replace(location.state.from)
            }
        }
    }

    handleCartUpdate = (cart) => {
        this.setState({
            cart,
        })
    }

    handlePostMessage = async (event) => {
        const { data } = event

        if (data?.wylieScript != 'companion') {
            return // not from our script
        }

        if (data.pong) {
            console.log('Found Wylie Companion')

            this.setState({
                companionReady: true,
            })
        }

        // set the embedOptions based on the attributes passed in
        Object.keys(this.supportedEmbedOptions).forEach(optionName => {
            if (optionName in data.attributes == false)  {
                return
            }

            const optionType = this.supportedEmbedOptions[optionName]
            const optionValue = data.attributes[optionName]

            if (optionValue !== null) {
                const processorFn = this.embedOptionProcessors[optionType]

                this.setState({
                    embedOptions: {
                        ...this.state.embedOptions,
                        [optionName]: processorFn(optionValue)
                    },
                })
            }
        })

        if (data.navigate && data.navigate in this.paths) {
            this.props.history.push(this.paths[data.navigate])
        }
    }

    messageCompanion = (msg) => {
        window.parent.postMessage({
            wylieScript: 'portal',
            ...JSON.parse(JSON.stringify(msg)),
        }, '*')
    }

    showMessage = (msg, options) => {
        if (this.state.embedOptions.minimalMode && this.state.companionReady) {
            this.messageCompanion({
                showMessage: { msg, options },
            })
        } else {
            this.props.enqueueSnackbar(msg, {
                autoHideDuration: 3000,
                anchorOrigin: {
                    vertical: 'top',
                    horizontal: 'center',
                },
                ...options,
            })
        }
    }

    showError = (err, options) => {
        if (this.state.embedOptions.minimalMode && this.state.companionReady) {
            this.messageCompanion({
                showError: { err, options },
            })
        } else {
            this.showMessage(err.message || err, {
                variant: 'error',
                persist: true,
                style: { whiteSpace: 'pre-line' },
                // eslint-disable-next-line react/display-name
                action: (key) => <Button onClick={() => { this.props.closeSnackbar(key) }}>Dismiss</Button>,
                ...options,
            })
        }
    }

    closeSnackbar = this.props.closeSnackbar

    render() {
        const { userType } = this.state

        return (
            <AppContext.Provider
                value={{
                    ...this.state,
                    messageCompanion: this.messageCompanion,
                    showMessage: this.showMessage,
                    showError: this.showError,
                    closeSnackbar: this.closeSnackbar,
                    fileset: this.fileset,
                    storeId: this.storeId,
                    paths: this.paths,
                    socket: this.socket,
                    iframeFixes: this.iframeFixes,
                }}
            >
                <StyledEngineProvider injectFirst>
                    <ThemeProvider theme={this.state.theme}>
                        {(!userType || userType == 'guest' || userType == 'user') &&
                            <App />
                        }
                        {userType == 'employee' &&
                            <EmployeeApp />
                        }
                        {userType == 'caregiver' &&
                            <CaregiverApp />
                        }
                    </ThemeProvider>
                </StyledEngineProvider>
            </AppContext.Provider>
        )
    }
}

function findUserType(user) {
    if (!user) {
        return null
    }

    if (user && user.isGuest) {
        return 'guest'
    }

    if (user && user.isCaregiver) {
        return 'caregiver'
    }

    if (user && user.isEmployee) {
        return 'employee'
    }

    return 'user'
}

export default withRouter(withSnackbar(AppContextProvider))

export function withAppContext(Component) {
    return function contextComponent(props) {
        return (
            <AppContext.Consumer>
                {context => <Component {...props} {...context} />}
            </AppContext.Consumer>
        )
    }
}
